import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ZardDialogRef, Z_MODAL_DATA } from '@/styles/components/dialog'
import { ICallbackDialogData, ICallbackDialogResult } from '../../models/dialog.models'
import { DEFAULT_DIALOG_TEXTS } from '../../../core/constants/button-text.constants'
import { DIALOG_CONSTANTS } from '../../../features/school-site/constants/proposal.constants'
import { SvgIconComponent } from '../svg-icon/svg-icon.component'
import { EiButtonComponent } from '../ei-button/ei-button.component'

@Component({
    selector: 'app-callback-dialog',
    templateUrl: './callback-dialog.component.html',
    styleUrls: ['./callback-dialog.component.scss'],
    imports: [FormsModule, SvgIconComponent, EiButtonComponent],
})
export class CallbackDialogComponent {
    dialogRef = inject<ZardDialogRef<CallbackDialogComponent>>(ZardDialogRef)
    data = inject<ICallbackDialogData>(Z_MODAL_DATA)

    @ViewChild('commentInput') commentInput?: ElementRef<HTMLTextAreaElement>

    readonly defaultTexts = DEFAULT_DIALOG_TEXTS
    readonly comment = signal('')
    readonly isCommentFocused = signal(false)
    readonly showMinLengthError = signal(false)
    maxLength: number = DIALOG_CONSTANTS.REQUEST_CALLBACK.maxLength
    minLength: number = DIALOG_CONSTANTS.REQUEST_CALLBACK.minLength
    minLengthMessage: string = DIALOG_CONSTANTS.REQUEST_CALLBACK.minLengthMessage

    constructor() {
        const dialogData = this.data
        if (dialogData.maxLength) {
            this.maxLength = dialogData.maxLength
        }
        // `!== undefined` so a caller can pass 0 for "no minimum".
        if (dialogData.minLength !== undefined) {
            this.minLength = dialogData.minLength
        }
        if (dialogData.minLengthMessage) {
            this.minLengthMessage = dialogData.minLengthMessage
        }
    }

    readonly isLabelFloating = () => this.isCommentFocused() || !!this.comment()?.length

    readonly trimmedCommentLength = () => this.comment().trim().length

    readonly isCommentBelowMin = () => {
        const trimmedLength = this.trimmedCommentLength()
        return !!trimmedLength && trimmedLength < this.minLength
    }

    readonly isConfirmDisabled = () => this.isCommentBelowMin()

    onCommentInput(value: string): void {
        this.comment.set(value.slice(0, this.maxLength))
        if (!this.isCommentBelowMin()) {
            this.showMinLengthError.set(false)
        }
    }

    onCommentFocus(): void {
        this.isCommentFocused.set(true)
    }

    onCommentBlur(): void {
        this.isCommentFocused.set(false)
    }

    handleConfirmClick(): void {
        if (this.isCommentBelowMin()) {
            this.showMinLengthError.set(true)
            return
        }

        const rawFromDom = this.commentInput?.nativeElement?.value
        const resolvedComment = (rawFromDom ?? this.comment() ?? '').trim()
        const dialogResult: ICallbackDialogResult = { confirmed: true, comment: resolvedComment }
        this.dialogRef.close(dialogResult)
    }

    handleCancelClick(): void {
        const dialogResult: ICallbackDialogResult = { confirmed: false, comment: '' }
        this.dialogRef.close(dialogResult)
    }
}
